import React from 'react';
import type { BlockInstance, TemplateModel } from '@troia-v3/types';
import { Select } from '@/shared/components/ui/Select';
import { FormField } from '@/shared/components/ui/FormField';
import { Icon } from '@/shared/icons';
import { DynamicFieldRenderer } from '../DynamicFieldRenderer';
import { getPageName } from './types';

interface PanelBlockProps {
  block: BlockInstance;
  model: TemplateModel;
  /** Páginas elegíveis como origem do espelhamento (mesmo blockId, não-espelhadas). */
  mirrorOptions: Array<{ label: string; value: string }>;
  onFieldChange: (name: string, value: unknown) => void;
  /** 'custom' remove o espelhamento; pageId ativa. */
  onMirrorChange: (value: string) => void;
}

/**
 * Formulário de um bloco. Quando espelhado (mirrorOf), os campos somem e um
 * aviso com "Desvincular" aparece — o config exibido/persistido segue a origem.
 */
export const PanelBlock: React.FC<PanelBlockProps> = ({
  block, model, mirrorOptions, onFieldChange, onMirrorChange,
}) => {
  const def = model.blocks.find((b) => b.id === block.blockId);
  if (!def) {
    return <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>Bloco não encontrado no modelo.</p>;
  }

  const values: Record<string, unknown> = { ...def.defaultConfig, ...block.config };
  const { mirrorOf } = block;

  return (
    <div className="space-y-4">
      {mirrorOptions.length > 0 && (
        <FormField
          label="Estilo deste bloco"
          description="Espelhar mantém este bloco idêntico ao da página de origem"
        >
          <Select
            value={block.mirrorOf || 'custom'}
            onChange={onMirrorChange}
            options={[
              { label: 'Personalizado', value: 'custom' },
              ...mirrorOptions,
            ]}
            data-testid="block-mirror-select"
          />
        </FormField>
      )}

      {mirrorOf ? (
        <div
          className="rounded-lg border px-3 py-3 space-y-2"
          style={{ borderColor: 'var(--theme-card-border)', backgroundColor: 'var(--theme-button-ghost-hover)' }}
          data-testid="block-mirror-notice"
        >
          <p className="text-sm flex items-start gap-2" style={{ color: 'var(--theme-card-text)' }}>
            <Icon name="link" className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--theme-primary)' }} />
            <span>
              Este bloco espelha o da página <strong>{getPageName(model, mirrorOf)}</strong>.
              Alterações feitas lá são aplicadas aqui automaticamente.
            </span>
          </p>
          <button
            type="button"
            onClick={() => onMirrorChange('custom')}
            className="text-sm font-medium underline"
            style={{ color: 'var(--theme-primary)' }}
            data-testid="block-mirror-unlink-button"
          >
            Desvincular e editar manualmente
          </button>
        </div>
      ) : (
        <DynamicFieldRenderer
          fields={def.configSchema}
          values={values}
          onChange={onFieldChange}
        />
      )}
    </div>
  );
};
